import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import * as serviceWorker from './serviceWorker';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import UserContextProvider from './context/UserContextProvider';
import ErrorBoundary from './components/ErrorBoundary';
import { ENV, GOOGLE_ANALYTICS_KEY } from './common/config';
import { initialize } from './quote-providers/lib/analytics/google';
import LoadingIndicator from './components/LoadingIndicator';

// Load google before anything else
if (ENV === 'production' && GOOGLE_ANALYTICS_KEY) {
  initialize(GOOGLE_ANALYTICS_KEY);
}

const AppLayout = lazy(() => import('./routes/AppLayout'));
const AdminLayout = lazy(() => import('./routes/AdminLayout'));
const LoginPage = lazy(() => import('./routes/LoginPage'));
const SignupPage = lazy(() => import('./routes/SignupPage'));
const LogoutPage = lazy(() => import('./routes/LogoutPage'));
const PublicLayout = lazy(() => import('./routes/PublicLayout'));
const ProfilePage = lazy(() => import('./routes/ProfilePage'));
const EightySevenPage = lazy(() => import('./routes/EightySevenPage'));
const TrainingPage = lazy(() => import('./routes/TrainingPage'));
const BetaPage = lazy(() => import('./routes/BetaPage'));
const AdminWaitlistPage = lazy(() => import('./routes/AdminWaitlistPage'));
const TermsPage = lazy(() => import('./routes/TermsPage')); // Added Terms of Service page
const PrivacyPage = lazy(() => import('./routes/PrivacyPage')); // Added Privacy Policy page
const WaitlistStatusPage = lazy(() => import('./routes/WaitlistStatusPage')); // Add WaitlistStatusPage import
const WaitlistPage = lazy(() => import('./routes/WaitlistPage')); // Add WaitlistPage import
const DashboardPage = lazy(() => import('./routes/DashboardPage'));

const host = window.location.host;
const count = host.includes('localhost') ? -1 : -2;
const subdomain = host.split('.').slice(0, count);
let is1987 = false;

if (subdomain.length > 0) {
  const sub = subdomain[0];

  switch (sub) {
    case '1987':
      is1987 = true;
  }
}

ReactDOM.render(
  <React.StrictMode>
    <ErrorBoundary>
      <UserContextProvider>
        <BrowserRouter>
          <Suspense
            fallback={<LoadingIndicator type="logo" fullScreen={true} />}
          >
            <Routes>
              <Route path="/" element={<PublicLayout />}>
                <Route path="login" element={<LoginPage />} />
                <Route path="waitlist" element={<WaitlistPage />} />
                <Route
                  path="waitlist/status"
                  element={<WaitlistStatusPage />}
                />
                <Route path="signup/*" element={<SignupPage />} />
                <Route path="terms" element={<TermsPage />} />{' '}
                {/* Added Terms route */}
                <Route path="privacy" element={<PrivacyPage />} />{' '}
                {/* Added Privacy route */}
                {/* Updated shared backtest route */}
                {is1987 ? (
                  <Route path="/" element={<EightySevenPage />} />
                ) : (
                  <Route path="/" element={<BetaPage />} />
                )}
              </Route>
              <Route path="/" element={<AppLayout />}>
                <Route path="profile" element={<ProfilePage />} />
                {/* Training Routes */}
                <Route path="training" element={<TrainingPage />} />
                {/* Dashboard Route (tab optional: /dashboard or /dashboard/overview) */}
                <Route path="dashboard/:tab?" element={<DashboardPage />} />
                <Route path="logout" element={<LogoutPage />} />
              </Route>
              {/* Admin Routes */}
              <Route path="/admin" element={<AdminLayout />}>
                <Route path="waitlist" element={<AdminWaitlistPage />} />
                {/* Removed invitations route since it's now consolidated */}
                <Route index element={<AdminWaitlistPage />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
      </UserContextProvider>
    </ErrorBoundary>
  </React.StrictMode>,
  document.getElementById('root')
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
